import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import FileUpload from '@/components/basicComponents/fileUpload';
import Label from '@/components/basicComponents/label';
import Tag from '@/components/basicComponents/tag';
import { AttachmentSubTypesEnum } from '@/components/transactions-page/generateSignaturePacket-page/addAttachmentFileModalPage';
import {
  attachmentMetadataToolTipContent,
  buildAttachmentSectionsWithFiles,
  openDocumentationAttachmentFile
} from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/rowComponents/attachmentsComponent';
import { StyledHeaderComponent } from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/styles';
import {
  IDocumentationAttachment,
  IRenderTemplatePage,
  IRenderTemplateRow
} from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/types';
import {
  evaluateDependencyItem,
  shouldRenderEmptyComponent
} from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/utils';
import { useResponsive } from '@/hooks/use-responsive';
import guid from '@/shared/utils/guid';
import { themeVar } from '@/styles/theme';
import { processExpectedAttachments } from '@trace-8/trace8-schema';
import _ from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { getCustomComponentValue } from '../utils';
import { BankAccountsPreview } from './bankAccountsPreview';
import { ContactsPreview } from './contactsPreview';

const shouldDisplayHeaderComponent = (page: IRenderTemplatePage, row: IRenderTemplateRow, formData: any): boolean => {
  const { group, config } = row;

  if (!group && config?.content) return true;

  const groupRows = page.rows.filter((r) => r.group === group && r.name !== row.name);

  return groupRows.some((r) => {
    const hasValidOrDependency: boolean = r?.dependsOn?.or
      ? r.dependsOn.or.some((item: any) => {
          return evaluateDependencyItem(formData?.[item.name], item);
        })
      : null;
    const hasValidAndDependency: boolean = r?.dependsOn?.and
      ? r.dependsOn.and.every((item: any) => {
          return evaluateDependencyItem(formData?.[item.name], item);
        })
      : null;

    if (shouldRenderEmptyComponent(r?.dependsOn, formData?.[r?.dependsOn?.name], hasValidOrDependency, hasValidAndDependency)) {
      return false;
    }

    switch (r.component) {
      case 'custom':
        return r?.fields?.some((field) => !!getCustomComponentValue(field, formData));

      case 'checkboxGroup':
        if ('sections' in r?.config) {
          return r?.config?.sections?.some((section: any) => section?.items?.some((item: any) => !!formData?.[item.name]));
        }
        if ('items' in r?.config) {
          return r?.config?.items?.some((item: any) => !!formData?.[item.name]);
        }
        break;
      case 'address':
        return Object.values(formData?.[r.name] || {}).some((value) => !!value);

      default:
        return !!formData?.[r.name];
    }
  });
};

interface PageRowsPreviewProps {
  row: IRenderTemplateRow;
  page: IRenderTemplatePage;
  currentPageFormData: any;
  formData: Record<string, any>;
  entityId: string;
  contacts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  bankAccounts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts'];
  uploadedFiles: IDocumentationAttachment[];
}

export const PageRowsPreview = ({
  row,
  page,
  currentPageFormData,
  formData,
  entityId,
  contacts,
  bankAccounts,
  uploadedFiles
}: PageRowsPreviewProps) => {
  const { isMobile } = useResponsive();
  const { label, config, dependsOn } = row;
  const hasValidOrDependency: boolean = dependsOn?.or
    ? dependsOn.or.some((item: any) => {
        return evaluateDependencyItem(currentPageFormData?.[item.name], item);
      })
    : null;
  const hasValidAndDependency: boolean = dependsOn?.and
    ? dependsOn.and.every((item: any) => {
        return evaluateDependencyItem(currentPageFormData?.[item.name], item);
      })
    : null;

  if (shouldRenderEmptyComponent(dependsOn, currentPageFormData?.[dependsOn?.name], hasValidOrDependency, hasValidAndDependency)) {
    return <></>;
  }

  switch (row.component) {
    case 'custom':
      return row.fields.map((field) => {
        const fieldValue = getCustomComponentValue(field, currentPageFormData);
        if (!fieldValue) return;

        if (field.component === 'checkbox') {
          return (
            <CheckboxGroupItem key={field.name}>
              <ApprovedIcon width={28} height={28} fill={themeVar('font.action')} />
              {getCustomComponentValue(field, currentPageFormData)}
            </CheckboxGroupItem>
          );
        }

        return (
          <TwoColumnWrapper key={field.name} isMobile={isMobile}>
            <GeneralInformationItem>{field.label}</GeneralInformationItem>
            <GeneralInformationItem isBold>{getCustomComponentValue(field, currentPageFormData) ?? ''}</GeneralInformationItem>
          </TwoColumnWrapper>
        );
      });
    case 'textarea':
      return (
        !!currentPageFormData?.[row.name] && (
          <TextareaComponentWrapper>
            <GeneralInformationItem>{row.label}</GeneralInformationItem>
            <GeneralInformationItem isBold>{currentPageFormData[row.name]}</GeneralInformationItem>
          </TextareaComponentWrapper>
        )
      );
    case 'checkboxGroup':
      if ('sections' in config) {
        return (
          config?.sections?.some((section: any) => section?.items?.some((item: any) => !!currentPageFormData?.[item.name])) && (
            <CheckboxGroupSectionsWrapper>
              {config?.sections?.map(
                (section: any) =>
                  section?.items?.some((item: any) => !!currentPageFormData?.[item.name]) && (
                    <div key={guid()}>
                      {section?.label && <Label size="md">{section.label}</Label>}
                      {section?.items?.map(
                        (item: any) =>
                          !!currentPageFormData?.[item.name] && (
                            <CheckboxGroupItem key={item.name}>
                              <ApprovedIcon width={28} height={28} fill={themeVar('font.action')} />
                              {item.label}
                            </CheckboxGroupItem>
                          )
                      )}
                    </div>
                  )
              )}
            </CheckboxGroupSectionsWrapper>
          )
        );
      }
      if ('items' in row?.config) {
        return (
          config?.items?.some((item: any) => !!currentPageFormData?.[item.name]) && (
            <div>
              {label && <Label size="md">{label}</Label>}
              {config?.items?.map(
                (item: any) =>
                  !!currentPageFormData?.[item.name] && (
                    <CheckboxGroupItem key={item.name}>
                      <ApprovedIcon width={28} height={28} fill={themeVar('font.action')} />
                      {item.label}
                    </CheckboxGroupItem>
                  )
              )}
            </div>
          )
        );
      }
      break;
    case 'header':
      if (shouldDisplayHeaderComponent(page, row, currentPageFormData)) {
        return <StyledHeaderComponent>{row.config?.content}</StyledHeaderComponent>;
      }
      break;

    case 'radio':
      return label ? (
        <TwoColumnWrapper isMobile={isMobile}>
          <GeneralInformationItem>{label}</GeneralInformationItem>
          <GeneralInformationItem isBold>
            {(config?.items?.find((item: any) => item.value === currentPageFormData?.[row.name])?.label || '') ?? ''}
          </GeneralInformationItem>
        </TwoColumnWrapper>
      ) : (
        <CheckboxGroupItem>
          <ApprovedIcon width={28} height={28} fill={themeVar('font.action')} />
          {(config?.items?.find((item: any) => item.value === currentPageFormData?.[row.name])?.label || '') ?? ''}
        </CheckboxGroupItem>
      );
    case 'address':
      return (
        currentPageFormData?.[row.name] &&
        Object.entries(currentPageFormData[row.name]).map(([key, value]) => (
          <TwoColumnWrapper key={key} isMobile={isMobile}>
            <GeneralInformationItem>{_.startCase(key)}</GeneralInformationItem>
            <GeneralInformationItem isBold>{value as string}</GeneralInformationItem>
          </TwoColumnWrapper>
        ))
      );
    case 'bankAccounts':
      return <BankAccountsPreview row={row} bankAccounts={bankAccounts} formData={currentPageFormData} />;
    case 'contacts':
      return <ContactsPreview row={row} contacts={contacts} formData={currentPageFormData} />;
    case 'attachments':
      const [schema, attachmentSections] = processExpectedAttachments(formData, contacts, config);
      const attachmentSectionsWithFiles = buildAttachmentSectionsWithFiles(attachmentSections, currentPageFormData?.[row.name] || {});
      return (
        <>
          <AttachmentsSectionsWrapper>
            {attachmentSectionsWithFiles?.map(
              (attachmentSection) =>
                !!attachmentSection?.expectedAttachmentsCount && (
                  <AttachmentsSection key={attachmentSection.name}>
                    <AttachmentsSectionHeader>
                      <StyledHeaderComponent>{attachmentSection.label}</StyledHeaderComponent>
                      <p>
                        {attachmentSection.expectedAttachmentsCount}/{attachmentSection?.expectedAttachments?.length} expected attachments
                      </p>
                    </AttachmentsSectionHeader>
                    {attachmentSection?.expectedAttachments?.map((expectedAttachment) => {
                      const attachments = currentPageFormData?.[row.name]?.[attachmentSection.name]?.[expectedAttachment.name] || [];
                      const currentAttachment = uploadedFiles?.find((file) => attachments?.includes(file.name));

                      return (
                        <React.Fragment key={expectedAttachment.name}>
                          {!!attachments?.length &&
                            attachments?.map((attachmentName: string, index: number) => {
                              return (
                                <FileUpload
                                  key={`${attachmentName}-${index}`}
                                  type="document"
                                  fileName={attachmentName}
                                  tooltipWidth={300}
                                  tooltipContent={attachmentMetadataToolTipContent(
                                    AttachmentSubTypesEnum[currentAttachment?.metadata?.type as keyof typeof AttachmentSubTypesEnum],
                                    currentAttachment?.metadata?.description
                                  )}
                                  additionalContent={<Tag title={expectedAttachment.label} size="sm" />}
                                  openFile={() => openDocumentationAttachmentFile(attachmentName, entityId)}
                                />
                              );
                            })}
                        </React.Fragment>
                      );
                    })}
                  </AttachmentsSection>
                )
            )}
          </AttachmentsSectionsWrapper>
        </>
      );
    default:
      return <></>;
  }
};

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '60%')};
  padding: 2px 10px;
`;

const TextareaComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  white-space: pre-line;
`;

export const GeneralInformationItem = styled.div<{ isBold?: boolean }>`
  font-family: Blinker;
  font-size: 16px;
  font-weight: ${({ isBold }) => (isBold ? 600 : 400)};
  line-height: 22.4px;
  color: ${themeVar('font.base')};
  padding: 4px;
`;

const CheckboxGroupSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CheckboxGroupItem = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  padding: 8px;
  color: ${themeVar('font.base')};
`;

const AttachmentsSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const AttachmentsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AttachmentsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;
